import { Capacitor } from '@capacitor/core';
import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import {
  AdminSellerContract,
  CreateSellerContractRequest,
  GetAllSellerContractsResponse,
  ManualSellerContractSignatureMethod,
} from '@/types/sellerContract';

export class SellerContractService {
  private static baseUrl = '/admin/seller-contracts';
  private static apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api/v1';

  // ---- Admin ---------------------------------------------------------------

  static async getAllSellerContracts(
    page: number = 1,
    limit: number = 50,
    status?: string,
    search?: string,
  ): Promise<GetAllSellerContractsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status && status !== 'all') params.append('status', status);
    if (search) params.append('search', search);

    const response = await api.get<{
      success: boolean;
      contracts: AdminSellerContract[];
      total: number;
      page: number;
      limit: number;
    }>(`${this.baseUrl}?${params.toString()}`);

    return {
      contracts: response.contracts || [],
      total: response.total,
      page: response.page,
      limit: response.limit,
    };
  }

  /** Returns the seller contract for a listing, or null when none exists. */
  static async getSellerContractByListingId(
    listingId: string,
  ): Promise<AdminSellerContract | null> {
    const response = await api.get<{
      success: boolean;
      contracts: AdminSellerContract[];
    }>(`${this.baseUrl}?listingId=${encodeURIComponent(listingId)}&limit=1`);
    return response.contracts?.[0] ?? null;
  }

  static async getSellerContract(id: string): Promise<AdminSellerContract> {
    const response = await api.get<{ success: boolean; data: AdminSellerContract }>(
      `${this.baseUrl}/${id}`,
    );
    return response.data;
  }

  /** Create (lazy upload pattern, same as buyer ContractService.createContract). */
  static async createSellerContract(
    request: CreateSellerContractRequest,
    replaceExisting: boolean = false,
  ): Promise<AdminSellerContract> {
    const { MediaService } = await import('../media/mediaService');

    const createResponse = await api.post<{ success: boolean; data: AdminSellerContract }>(
      this.baseUrl,
      {
        contractNumber: request.contractNumber,
        listingId: request.listingId,
        replaceExisting,
      },
    );
    const created = createResponse.data;

    if (request.contractFile && created.id) {
      try {
        const metadata = {
          originalName: request.contractFile.name,
          mimeType: request.contractFile.type,
          sizeBytes: request.contractFile.size,
        };
        const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
          'sellerContract',
          created.id,
          metadata,
        );
        await MediaService.uploadFileToS3(uploadUrl, request.contractFile);
        const mediaResponse = await MediaService.createMediaAfterUpload(s3Key, metadata, {
          sellerContractId: created.id,
        });
        await MediaService.updateMediaStatus(mediaResponse.id, 'UPLOADED');

        // Upload immediately requests BankID signature (email + in-app + push to seller).
        return await this.requestSignature(created.id);
      } catch (error) {
        console.error('Failed to upload/request seller contract signature:', error);
        throw error;
      }
    }

    return created;
  }

  static async updateSellerContract(
    id: string,
    data: { contractNumber?: string },
  ): Promise<AdminSellerContract> {
    const response = await api.put<{ success: boolean; data: AdminSellerContract }>(
      `${this.baseUrl}/${id}`,
      { contractNumber: data.contractNumber },
    );
    return response.data;
  }

  static async deleteSellerContract(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`);
  }

  static async sendSellerContract(id: string): Promise<{ success: boolean; message: string }> {
    return api.post<{ success: boolean; message: string }>(`${this.baseUrl}/${id}/send`);
  }

  static async requestSignature(id: string): Promise<AdminSellerContract> {
    const response = await api.post<{ success: boolean; data: AdminSellerContract }>(
      `${this.baseUrl}/${id}/request-signature`,
      {},
    );
    return response.data;
  }

  static async markSignature(
    id: string,
    method: ManualSellerContractSignatureMethod,
    note?: string,
  ): Promise<AdminSellerContract> {
    const response = await api.post<{ success: boolean; data: AdminSellerContract }>(
      `${this.baseUrl}/${id}/mark-signature`,
      { method: method.toUpperCase(), note },
    );
    return response.data;
  }

  // ---- Seller (BankID) -----------------------------------------------------

  static getBankIdSignatureStartUrl(contractId: string): string {
    return `${this.apiBaseUrl.replace(/\/$/, '')}/listings/seller-contracts/${contractId}/sign-bankid`;
  }

  static async getNativeBankIdSignatureContinueUrl(contractId: string): Promise<string> {
    const data = await api.post<{ success: boolean; continueUrl: string }>(
      `/listings/seller-contracts/${contractId}/sign-bankid/native-start`,
      { nativePlatform: Capacitor.getPlatform() },
    );
    return data.continueUrl;
  }

  /** Finalize BankID signature when the Idura webhook is delayed/missing. */
  static async finalizePendingBankIdSignature(params?: {
    signatoryId?: string | null;
    signatureOrderId?: string | null;
  }): Promise<{ signed: boolean; contractId?: string | null; reason?: string | null }> {
    const data = await api.post<{
      success: boolean;
      signed: boolean;
      contractId?: string | null;
      reason?: string | null;
    }>('/listings/seller-contracts/signatures/finalize-pending', {
      signatoryId: params?.signatoryId || undefined,
      signatureOrderId: params?.signatureOrderId || undefined,
    });
    return {
      signed: Boolean(data.signed),
      contractId: data.contractId ?? null,
      reason: data.reason ?? null,
    };
  }
}
